import React, { useEffect, useRef, useState } from 'react';
import MemberLogoConvergence from '../components/MemberLogoConvergence';
import { members } from '../data/members';

const stats = [
  { value: 20, suffix: '+', label: 'Member Organizations' },
  { value: 4, suffix: '', label: 'Major Disaster Operations' },
  { value: 2, suffix: '', label: 'National WHD Events Led' },
  { value: 2026, suffix: '', label: 'Year of Official Registration' },
];

const milestones = [
  {
    icon: '🤝',
    year: 'Aug 2023',
    title: 'Network Founded',
    desc: 'Humanitarian actors at World Humanitarian Day at Karimjee Hall identified coordination gaps and agreed to establish DINGONET as a structured NGO network.',
    img: '',
  },
  {
    icon: '🌍',
    year: '2024',
    title: 'World Humanitarian Day - Dar es Salaam',
    desc: 'DINGONET coordinated the national WHD commemoration at JICC, convening government institutions, UN agencies, NGOs and development partners - officiated by PMO-DMD.',
    img: '/assets/event.jpeg',
  },
  {
    icon: '🌊',
    year: '2024',
    title: 'Flood Response - Kibiti & Rufiji',
    desc: 'DINGONET mobilized member organizations for joint Damage and Needs Assessments and coordinated response to severe flooding in Kibiti and Rufiji districts alongside government partners.',
    img: '/assets/rufiji.jpeg',
  },
  {
    icon: '🏙️',
    year: '2024',
    title: 'Kariakoo Building Collapse Response',
    desc: 'Following the urban disaster in Dar es Salaam, DINGONET members delivered rapid coordination, information sharing, and emergency response - demonstrating urban resilience capacity.',
    img: '/assets/kariakoo-pic3.jpg',
  },
  {
    icon: '🏛️',
    year: '2025',
    title: 'World Humanitarian Day - Dodoma',
    desc: "DINGONET led WHD 2025 in Dodoma, officiated by the Prime Minister's Office, reinforcing national-level recognition of coordinated humanitarian action.",
    img: '/assets/event2.jpeg',
  },
  {
    icon: '📋',
    year: 'Jan 2026',
    title: 'Official NGO Registration',
    desc: 'DINGONET was officially registered under Registration No. 8894 by the Registrar of NGOs in Tanzania, cementing its legal status as a national coordination platform.',
    img: '/assets/certificate.png',
    imgPortrait: true,
  },
];

function useInView(options = {}) {
  const [isInView, setIsInView] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        if (options.once !== false) {
          observer.unobserve(entry.target);
        }
      } else if (options.once === false) {
        setIsInView(false);
      }
    }, options.config || { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

    observer.observe(element);
    return () => observer.disconnect();
  }, [options.once, options.config]);

  return [elementRef, isInView];
}

function useCounter(targetValue, duration, shouldStart) {
  const [count, setCount] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!shouldStart) return undefined;

    let startTime = null;
    const startValue = 0;

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const value = Math.floor(startValue + (targetValue - startValue) * progress);
      setCount(value);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [targetValue, duration, shouldStart]);

  return count;
}

function StatCard({ stat, shouldStart }) {
  const count = useCounter(stat.value, 1400, shouldStart);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
      <p className="text-5xl font-black text-[#00E5CC]">
        {count}
        {stat.suffix}
      </p>
      <p className="mt-2 text-xs uppercase tracking-widest text-slate-400">{stat.label}</p>
    </div>
  );
}

function TimelineItem({ item, index }) {
  const [itemRef, isInView] = useInView({ once: true, config: { threshold: 0.2, rootMargin: '0px 0px -8% 0px' } });
  const isLeft = index % 2 === 0;
  const card = (
    <div className="group overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 transition-all duration-500 hover:border-[#00E5CC]/40 hover:shadow-[0_0_40px_rgba(0,229,204,0.12)]">
      {item.img ? (
        <div
          className={
            item.imgPortrait
              ? 'overflow-hidden bg-slate-950/60 p-4 md:p-6'
              : 'h-48 overflow-hidden'
          }
        >
          <img
            src={item.img}
            alt={item.title}
            className={
              item.imgPortrait
                ? 'mx-auto block h-auto w-full max-w-xs object-contain md:max-w-sm'
                : 'h-full w-full object-cover transition-transform duration-700 group-hover:scale-105'
            }
          />
        </div>
      ) : null}
      <div className="p-6">
        <span className="inline-flex rounded-full border border-[#00E5CC]/20 bg-[#00E5CC]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#00E5CC]">
          {item.year}
        </span>
        <h3 className="mt-4 text-lg font-bold text-white">{item.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.desc}</p>
      </div>
    </div>
  );

  return (
    <div
      ref={itemRef}
      className="relative grid grid-cols-1 gap-6 md:grid-cols-[1fr_56px_1fr] md:items-center"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateX(0px)' : `translateX(${isLeft ? '-40px' : '40px'})`,
        transition: `opacity 700ms ease, transform 700ms ease`,
        transitionDelay: `${index * 80}ms`,
      }}
    >
      <div className="hidden md:col-start-1 md:block md:pr-10">
        {isLeft ? card : <div aria-hidden="true" />}
      </div>

      <div className="pointer-events-none z-20 hidden md:flex md:col-start-2 md:items-center md:justify-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border-4 border-[#0B1629] bg-gradient-to-br from-[#00E5CC] to-[#00BFA5] text-lg shadow-lg shadow-[#00E5CC]/30">
          {item.icon}
        </div>
      </div>

      <div className={`md:hidden ${isLeft ? 'pr-6' : 'pl-6'}`}>
        <div className={`mb-4 flex items-center gap-3 ${isLeft ? '' : 'justify-end'}`}>
          {!isLeft ? (
            <span className="inline-flex rounded-full border border-[#00E5CC]/20 bg-[#00E5CC]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#00E5CC]">
              {item.year}
            </span>
          ) : null}
          <div className="flex h-11 w-11 items-center justify-center rounded-full border-4 border-[#0B1629] bg-gradient-to-br from-[#00E5CC] to-[#00BFA5] text-base shadow-lg shadow-[#00E5CC]/30">
            {item.icon}
          </div>
          {isLeft ? (
            <span className="inline-flex rounded-full border border-[#00E5CC]/20 bg-[#00E5CC]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#00E5CC]">
              {item.year}
            </span>
          ) : null}
        </div>
        <div className={`${isLeft ? '' : 'ml-auto'} max-w-[540px]`}>{card}</div>
      </div>

      <div className="hidden md:col-start-3 md:block md:pl-10">
        {isLeft ? <div aria-hidden="true" /> : card}
      </div>
    </div>
  );
}

function MemberCard({ item, index, visible }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const initials = item.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  const isInternational = item.type === 'international';
  const showLogo = item.logo && !logoFailed;

  return (
    <article
      className="group cursor-default rounded-xl border border-white/10 bg-white/5 p-5 transition-all duration-300 hover:border-[#00E5CC]/40 hover:bg-[#00E5CC]/5 hover:shadow-[0_0_20px_rgba(0,229,204,0.08)]"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.95)',
        transition: `opacity 500ms ease, transform 500ms ease`,
        transitionDelay: `${index * 30}ms`,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#00E5CC]/20 ${
            showLogo ? 'bg-white p-1.5' : 'bg-gradient-to-br from-[#00E5CC]/20 to-[#00BFA5]/10 text-sm font-bold text-[#00E5CC]'
          }`}
        >
          {showLogo ? (
            <img
              src={`/assets/${encodeURIComponent(item.logo)}`}
              alt=""
              className="h-full w-full object-contain"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            initials
          )}
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] ${
            isInternational
              ? 'border border-blue-500/20 bg-blue-500/10 text-blue-400'
              : 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-400'
          }`}
        >
          {isInternational ? 'International' : 'National'}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold leading-snug text-white">{item.name}</h3>
      <p className="mt-1 text-[10px] uppercase tracking-wide text-slate-500">{item.focus}</p>
      <p className="mt-3 border-t border-white/5 pt-3 text-[10px] text-slate-600">
        {item.status || 'Member since 2024'}
      </p>
    </article>
  );
}

export default function AchievementsPage({ onNavigate }) {
  const [statsRef, statsInView] = useInView({ once: true, config: { threshold: 0.25 } });
  const [membersGridRef, membersGridInView] = useInView({ once: true, config: { threshold: 0.12, rootMargin: '0px 0px -8% 0px' } });

  const goToJoinSection = () => onNavigate?.('members', 'join-dingonet');
  const goToContact = () => onNavigate?.('contact', 'get-in-touch');

  return (
    <div className="min-h-screen bg-[#0B1629] text-white">
      <main>
        <section className="relative overflow-hidden px-5 pb-20 pt-32 lg:px-8">
          <div className="pointer-events-none absolute left-1/2 top-8 h-72 w-72 -translate-x-1/2 rounded-full bg-cyan-500/5 blur-3xl" />
          <div className="pointer-events-none absolute right-8 top-16 h-80 w-80 rounded-full bg-cyan-500/5 blur-3xl" />

          <div className="mx-auto max-w-4xl text-center">
            <span className="inline-flex rounded-full border border-[#00E5CC]/30 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#00E5CC]">
              Achievements
            </span>
            <h1 className="mt-6 text-5xl font-black leading-tight md:text-7xl">
              Our Impact &amp; <span className="text-[#00E5CC]">Key Milestones</span>
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-slate-400 md:text-lg">
              Since 2023, DINGONET has united Tanzania&apos;s humanitarian actors in coordinated disaster preparedness,
              emergency response, and community resilience - with measurable results on the ground.
            </p>
          </div>
        </section>

        <section ref={statsRef} className="border-t border-white/[0.07] px-5 py-16 lg:px-8">
          <div className="mx-auto grid max-w-5xl grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
            {stats.map((stat) => (
              <StatCard key={stat.label} stat={stat} shouldStart={statsInView} />
            ))}
          </div>
        </section>

        <section className="border-t border-white/[0.07] px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-black md:text-5xl">
              From Founding to <span className="text-[#00E5CC]">National Force</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-400">
              Key moments that shaped DINGONET into a trusted national platform for coordinated humanitarian action.
            </p>
          </div>

          <div className="relative mx-auto mt-14 max-w-6xl space-y-10 md:space-y-14">
            <div className="pointer-events-none absolute bottom-0 left-1/2 top-0 hidden w-px -translate-x-1/2 bg-gradient-to-b from-[#00E5CC]/40 via-[#00E5CC]/10 to-transparent md:block" />
            {milestones.map((item, index) => (
              <TimelineItem key={`${item.year}-${item.title}`} item={item} index={index} />
            ))}
          </div>
        </section>

        <section className="border-t border-white/[0.07] px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-black md:text-5xl">
              Our <span className="text-[#00E5CC]">Member Network</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-slate-400">
              DINGONET brings together local and international organizations committed to disaster management and
              humanitarian action in Tanzania.
            </p>
          </div>

          <div
            ref={membersGridRef}
            className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {members.map((member, index) => (
              <MemberCard key={member.name} item={member} index={index} visible={membersGridInView} />
            ))}
          </div>

          <MemberLogoConvergence members={members} />
        </section>

        <section className="border-t border-white/[0.07] px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-3xl rounded-3xl border border-[#00E5CC]/20 bg-gradient-to-br from-[#00E5CC]/10 to-[#00BFA5]/5 p-10 text-center md:p-12">
            <h2 className="text-3xl font-black text-white">Join the Network</h2>
            <p className="mx-auto mt-4 max-w-2xl text-slate-400">
              Membership is open to all organizations actively engaged in disaster management and humanitarian action
              in Tanzania.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={goToJoinSection}
                className="rounded-full bg-[#00E5CC] px-8 py-3 font-bold text-[#0B1629] transition-colors hover:bg-[#00BFA5]"
              >
                Become a Member
              </button>
              <button
                type="button"
                onClick={goToContact}
                className="rounded-full border border-white/20 px-8 py-3 text-white transition-colors hover:border-white/40"
              >
                Contact Secretariat
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 px-5 py-8 text-center text-sm text-slate-500 lg:px-8">
        <p>
          DINGONET Secretariat (Hosted by RAPID-Tanzania) - P.O. Box 11189, Dar es Salaam - info@dingonet.org -
          Executive Director 0757724717 · Lead of Advocacy and Communication 0744388179
        </p>
        <p className="mt-2">Reg. No. 8894 - Registered January 2026</p>
      </footer>
    </div>
  );
}
